/**
 * State of the local vault: present or not, locked or not, plus the unlocked session.
 *
 * The session lives here and only here, in memory. It is never serialised, never written to
 * localStorage, and it is destroyed both on logout and on idle expiry. The `src/wallet`
 * module separately guarantees the private key cannot be read back out of a session.
 *
 * During the transition, having no vault is not blocking: the backend can still sign for
 * accounts migrated from the custodial world. `WalletContext` picks the path.
 */

import React, {
  createContext, useContext, useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useAuth } from './AuthContext';
import {
  hasVault, unlockVault, createVault, deleteVault, lock as lockSession, isUnlockedSession,
  importFromHex, importFromMnemonic, isValidMnemonic, isPrivateKeyHex,
  WrongPasswordError, MIN_PASSWORD_LENGTH,
  type Session,
} from '../wallet';
import { rememberVault } from '../services/vault-index';

/**
 * Time after which the vault locks itself again.
 *
 * ⚠️ DELIBERATELY NOT AN IDLE TIMER, and this is not an oversight to repair. Mouse and
 * keyboard activity do not reset it: it is only re-armed by `touch()`, on unlock, on vault
 * creation/adoption, and after signing a bridge order. Buying traffic, paying for premium,
 * sending from the wallet and accepting a transfer all sign WITHOUT re-arming it. The key
 * therefore lives a bounded time from the moment the password was typed, whatever happens
 * on screen — a hard cap, not a session that stretches with use. Wiring this to activity
 * would mean an open tab holds a signing key indefinitely.
 * Raising the number widens the window; it does not, and must not, change that.
 */
const AUTO_LOCK_MS = 30 * 60 * 1000;

export type VaultStatus = 'loading' | 'absent' | 'locked' | 'unlocked';

interface VaultContextType {
  status: VaultStatus;
  session: Session | null;
  /** True for as long as the account has no vault on this device. */
  needsSetup: boolean;
  error: string | null;
  clearError: () => void;

  unlock: (password: string) => Promise<void>;
  /** Creates the vault from a 24-word phrase or a 64-character hex key. */
  setup: (secret: string, password: string) => Promise<void>;
  lockNow: () => void;
  /** Adopts a session opened during sign-in, so the password is not asked for twice. */
  adoptSession: (session: Session) => void;
  forget: () => Promise<void>;
  /** Pushes back the auto-lock — called after every signature. */
  touch: () => void;
}

const VaultContext = createContext<VaultContextType | null>(null);

export const useVault = () => {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used within VaultProvider');
  return ctx;
};

export const VaultProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const accountId = user?.id || null;

  const [status, setStatus] = useState<VaultStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror of the session, readable from the effect without re-running it on every change.
  const sessionRef = useRef<Session | null>(null);
  useEffect(() => { sessionRef.current = session; }, [session]);

  const clearError = useCallback(() => setError(null), []);

  const clearTimer = useCallback(() => {
    if (lockTimer.current) {
      clearTimeout(lockTimer.current);
      lockTimer.current = null;
    }
  }, []);

  const lockNow = useCallback(() => {
    clearTimer();
    setSession((current) => {
      if (current) lockSession(current);
      return null;
    });
    setStatus((s) => (s === 'unlocked' ? 'locked' : s));
  }, [clearTimer]);

  const touch = useCallback(() => {
    clearTimer();
    lockTimer.current = setTimeout(lockNow, AUTO_LOCK_MS);
  }, [clearTimer, lockNow]);

  // Whether a vault exists for the current account. Re-evaluated on every account change.
  useEffect(() => {
    let cancelled = false;
    if (!accountId) {
      lockNow();
      setStatus('loading');
      return () => { cancelled = true; };
    }
    (async () => {
      // On sign-in, applySession moves accountId from null to the account id, which
      // re-runs this effect — and its async answer lands AFTER adoptSession, overwriting
      // the 'unlocked' it just set. The user would then be asked again for the password
      // they had only just typed.
      if (sessionRef.current && sessionRef.current.accountId === accountId
          && isUnlockedSession(sessionRef.current)) {
        if (!cancelled) setStatus('unlocked');
        return;
      }
      try {
        const present = await hasVault(accountId);
        if (cancelled) return;
        if (sessionRef.current && sessionRef.current.accountId === accountId
            && isUnlockedSession(sessionRef.current)) {
          setStatus('unlocked');
          return;
        }
        setStatus(present ? 'locked' : 'absent');
      } catch {
        if (!cancelled) setStatus('absent');
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, lockNow]);

  // Logging out must destroy the in-memory session, not just the tokens.
  useEffect(() => {
    if (!user) lockNow();
  }, [user, lockNow]);

  // Lock when the tab closes: a session never survives a reload.
  //
  // We wipe the key directly rather than going through lockNow(): that one calls setState,
  // so React repaints the unlock screen ON THE PAGE BEING LEFT — the user sees the prompt
  // pop up at the exact moment they hit refresh, before the new page even starts. React
  // state has no reason to be updated here anyway: it dies with the document. Only wiping
  // the key matters.
  useEffect(() => {
    const onHide = () => {
      clearTimer();
      if (sessionRef.current) lockSession(sessionRef.current);
      sessionRef.current = null;
    };
    window.addEventListener('beforeunload', onHide);
    return () => window.removeEventListener('beforeunload', onHide);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const unlock = useCallback(async (password: string) => {
    if (!accountId) throw new Error('Not signed in');
    setError(null);
    try {
      const s = await unlockVault(accountId, password);
      setSession(s);
      setStatus('unlocked');
      touch();
    } catch (err) {
      const message = err instanceof WrongPasswordError
        ? 'Wrong password'
        : (err as Error).message || 'Could not unlock your wallet';
      setError(message);
      throw new Error(message);
    }
  }, [accountId, touch]);

  const setup = useCallback(async (secret: string, password: string) => {
    if (!accountId) throw new Error('Not signed in');
    setError(null);

    const trimmed = secret.trim();
    if (!isPrivateKeyHex(trimmed) && !isValidMnemonic(trimmed)) {
      const message = 'That does not look like a recovery key (64 hex characters) or a 24-word phrase';
      setError(message);
      throw new Error(message);
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      const message = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
      setError(message);
      throw new Error(message);
    }

    // The key must match the account: otherwise we would lock the user into a vault that
    // signs for a different party, and the mistake would only surface on their first send.
    const identity = isPrivateKeyHex(trimmed) ? importFromHex(trimmed) : importFromMnemonic(trimmed);
    if (user?.canton_party_id && !user.canton_party_id.endsWith(identity.fingerprint)) {
      const message = 'This key does not match the wallet on your account';
      setError(message);
      throw new Error(message);
    }

    try {
      await createVault(accountId, trimmed, password, { overwrite: true });
      // The local directory lets the sign-in page tell, from the email alone,
      // that this browser already holds the key — otherwise it would ask for it every time.
      if (user?.email) rememberVault(user.email, accountId);
      const s = await unlockVault(accountId, password);
      setSession(s);
      setStatus('unlocked');
      touch();
    } catch (err) {
      const message = (err as Error).message || 'Could not create your wallet vault';
      setError(message);
      throw new Error(message);
    }
  }, [accountId, user?.canton_party_id, user?.email, touch]);

  const adoptSession = useCallback((s: Session) => {
    setSession(s);
    setStatus('unlocked');
    touch();
  }, [touch]);

  const forget = useCallback(async () => {
    if (!accountId) return;
    lockNow();
    await deleteVault(accountId).catch(() => {});
    setStatus('absent');
  }, [accountId, lockNow]);

  const value = useMemo(() => ({
    status,
    session,
    needsSetup: status === 'absent',
    error,
    clearError,
    unlock,
    setup,
    lockNow,
    adoptSession,
    forget,
    touch,
  }), [status, session, error, clearError, unlock, setup, lockNow, adoptSession, forget, touch]);

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
};
